import { createSelector } from 'reselect';
import { graphColors, colorList, colorsCI } from '../../constants/index';
import moment from 'moment';
import { strings } from '../../localization/index';
import {
    switchList, selectedSwitch
} from '../switch/selectors';
import {
    getDevList
} from '../devices/selectors';
import {
    getLegendsVisibility
} from '../app/selector';

const getMonitoringLoading = state => state.monitoring.get('isLoading');

const getMonitoringLive = state => state.monitoring.get('isLive');

const getGraphData = state => state.monitoring.get('data');

const getMonitoringFrom = state => state.monitoring.get('from');

const getMonitoringTo = state => state.monitoring.get('to');

const getLastTs = state => state.monitoring.get('lastTs');

const getMonitoringSetTimestamp = state => state.monitoring.get('setTimestamp');

/* ====================== SELECTORS ======================== */

export const getMonitoringIsLoading = createSelector(
    [getMonitoringLoading],
    isLoading => isLoading
);

export const getMonitoringIsLive = createSelector(
    [getMonitoringLive],
    isLive => isLive
);

export const getMonitoringLastTs = createSelector(
    [getLastTs],
    lastTs => lastTs
);

export const getMonitoringBounds = createSelector(
    [getMonitoringFrom, getMonitoringTo, getMonitoringSetTimestamp],
    (from , to, ts) => ({from, to, ts})
);

export const getMonitoringBoundsMoment = createSelector(
    [getMonitoringFrom, getMonitoringTo, getLastTs],
    (from , to, lts) => {
        const currentTs = moment().valueOf();
        return {from: moment(currentTs - from), to: moment(currentTs - to), lts};
    }
);

export const getMonitoringRawData = createSelector(
    [getGraphData],
    data => data && data.toJS ? data.toJS() : data
);

export const getServerTimeDifference = createSelector(
    [getGraphData],
    data => {
        const serverTs = data && data.toJS ? data.get('serverTs') : 0;
        const clientTs = moment().valueOf();
        if(serverTs > 0) return Math.abs(serverTs - clientTs);
        return 0;
    }
);

export const getMonitoringGraphData = createSelector(
    [getGraphData, getMonitoringBounds, getMonitoringIsLive, getLegendsVisibility],
    (data, graphBounds, isLive, visibility) => {

        const rawData = data && data.toJS ? data.toJS() : data;

        const currentTs = isLive ? moment().valueOf() : graphBounds.ts;

        const devicesAndSubmeters = [];

        let colorIndex = 0;

        if (rawData && rawData.subMeterAndDeviceData) {
            for (const key in rawData.subMeterAndDeviceData) {
                if (Object.prototype.hasOwnProperty.call(rawData.subMeterAndDeviceData, key)) {
                    const raw = rawData.subMeterAndDeviceData[key];
                    const label = raw[0] === undefined || raw[0].name === "others" ? strings.training.others : raw[0].name;
                    const isMeter = raw[0] !== undefined ? raw[0].meterId >= 0 : false;
        
                    devicesAndSubmeters.push({                        
                        hidden: false,
                        label,
                        backgroundColor: colorsCI.transparent[colorIndex],
                        borderColor: colorsCI.base[colorIndex],
                        borderWidth: 1,
                        lineTension: 0,
                        data: raw.map(item => ({x: item.ts, y: item.watt})),
                        isMeter,
                    });

                    colorIndex += 1;
                }                
            }            
        }      

        const powerData = rawData && rawData.powerData ? rawData.powerData.filter(item => item.id <= currentTs - graphBounds.to && item.id >= currentTs - graphBounds.from) : [];
        const solarData = rawData && rawData.solarData ? rawData.solarData.filter(item => item.id <= currentTs - graphBounds.to && item.id >= currentTs - graphBounds.from) : [];
        const batteryData = rawData && rawData.batteryData ? rawData.batteryData.filter(item => item.id <= currentTs - graphBounds.to && item.id >= currentTs - graphBounds.from) : [];
        const onOff = rawData && rawData.onOff ? rawData.onOff.filter(
            item => item.id <= currentTs - graphBounds.to && item.id >= currentTs - graphBounds.from && (
                // display classifications only if phase legend is visible
                visibility[3] && item.phase === 1 ||
                visibility[4] && item.phase === 2 ||
                visibility[5] && item.phase === 3
            )
        ) : [];
        return {
            datasets: [{
                hidden: !visibility[0],
                label: 'Classifications',
                type: 'scatter',
                data: onOff.map(item => {
                    let y = item.watt;
                    const data = powerData.filter(itm => itm.id > item.id).sort((a,b) => a.id - b.id);
                    if(data.length > 0)
                        switch(item.phase){
                            case 1:
                                y = data[0].p1;
                                break;
                            case 2:
                                y = data[0].p2;
                                break;
                            case 3:
                                y = data[0].p3;
                                break;
                            default:
                        }

                    return {x: item.id, y, wt: item.watt, vr: item.var, duration: item.duration, device: item.device, phase: item.phase};
                })
            },{
                hidden: !visibility[1],
                label: 'SOLAR',
                backgroundColor: graphColors.solar_fill,
                borderColor: graphColors.solar_line,
                borderWidth: 1,
                lineTension: 0,
                data: solarData.map(item => ({x: item.id, y: item.watt}))
            },{
                hidden: !visibility[2],
                label: 'SUM',
                backgroundColor: graphColors.oa_fill,
                borderColor: graphColors.oa_line,
                borderWidth: 1,
                lineTension: 0,
                data: powerData.map(item => ({x: item.id, y: item.p1+item.p2+item.p3}))
            },{
                hidden: !visibility[3],
                label: 'L1',
                backgroundColor: graphColors.p1_fill,
                borderColor: graphColors.p1_line,
                borderWidth: 1,
                lineTension: 0,
                data: powerData.map(item => ({x: item.id, y: item.p1}))
            },{
                hidden: !visibility[4],
                label: 'L2',
                backgroundColor: graphColors.p2_fill,
                borderColor: graphColors.p2_line,
                borderWidth: 1,
                lineTension: 0,
                data: powerData.map(item => ({x: item.id, y: item.p2}))
            },{
                hidden: !visibility[5],
                label: 'L3',
                backgroundColor: graphColors.p3_fill,
                borderColor: graphColors.p3_line,
                borderWidth: 1,
                lineTension: 0,
                data: powerData.map(item => ({x: item.id, y: item.p3}))
            },{
                hidden: !visibility[6],
                label: 'VAR3',
                backgroundColor: graphColors.p3_var,
                borderColor: graphColors.p3_line,
                borderWidth: 1,
                lineTension: 0,
                borderDash: [1,2],
                data: powerData.map(item => ({x: item.id, y: item.var3}))
            },{
                hidden: !visibility[7],
                label: 'VAR2',
                backgroundColor: graphColors.p2_var,
                borderColor: graphColors.p2_line,
                borderWidth: 1,
                lineTension: 0,
                borderDash: [1,2],
                data: powerData.map(item => ({x: item.id, y: item.var2}))
            },{
                hidden: !visibility[8],
                label: 'VAR1',
                backgroundColor: graphColors.p1_var,
                borderColor: graphColors.p1_line,
                borderWidth: 1,
                lineTension: 0,
                borderDash: [1,2],
                data: powerData.map(item => ({x: item.id, y: item.var1}))            
            },{
                hidden: !visibility[9],
                label: 'BATT',
                backgroundColor: graphColors.battery_fill,
                borderColor: graphColors.battery_line,
                borderWidth: 1,
                lineTension: 0,
                data: batteryData.map(item => ({x: item.id, y: item.watt, soc: item.soc}))
            },{
                label: 'HIDDEN',
                fill: false,
                borderColor: '#EEE',
                borderWidth: 1,
                lineTension: 0,
                data: [{x: currentTs - graphBounds.to, y: 0}, {x: currentTs - graphBounds.from, y: 0}]
            }],
            devices: rawData && rawData.deviceData ? rawData.deviceData : [],
            productionAndBattery: rawData && rawData.productionAndBatteryData ? rawData.productionAndBatteryData : [],
            devicesAndSubmeters
        };
    }
);

export const getMonitoringAddonGraphData = createSelector(
    [getGraphData, getMonitoringBounds],
    (data, graphBounds) => {

        const rawData = data && data.toJS ? data.toJS() : data;
        const currentTs = moment().valueOf();
        const premiumData = rawData && rawData.premiumData ? rawData.premiumData.filter(item => item.id <= currentTs - graphBounds.to && item.id >= currentTs - graphBounds.from) : [];

        return {
            datasets: [{
                label: 'L1',
                backgroundColor: graphColors.p1_fill,
                borderColor: graphColors.p1_line,
                borderWidth: 1,
                lineTension: 0,
                data: premiumData.map(item => ({x: item.id, y: item.p1}))
            },{
                label: 'L2',
                backgroundColor: graphColors.p2_fill,
                borderColor: graphColors.p2_line,
                borderWidth: 1,
                lineTension: 0,
                data: premiumData.map(item => ({x: item.id, y: item.p2}))
            },{
                label: 'L3',
                backgroundColor: graphColors.p3_fill,
                borderColor: graphColors.p3_line,
                borderWidth: 1,
                lineTension: 0,
                data: premiumData.map(item => ({x: item.id, y: item.p3}))
            },{
                label: 'HIDDEN',
                fill: false,
                borderColor: '#EEE',
                borderWidth: 1,
                lineTension: 0,
                data: [{x: currentTs - graphBounds.to, y: 0}, {x: currentTs - graphBounds.from, y: 0}]
            }]
        };
    }
);

export const getMonitoringSingleGraphData = createSelector(
    [getGraphData, getMonitoringBounds, switchList, selectedSwitch, getDevList],
    (data, graphBounds, swLst, selectedSw, devList) => {

        const rawData = data && data.toJS ? data.toJS() : data;
        const swList = swLst && swLst.toJS ? swLst.toJS() : swLst;
        const currentTs = moment().valueOf();
        const powerSingle = rawData && rawData.powerSingle ? rawData.powerSingle.filter(item => item.id <= currentTs - graphBounds.to && item.id >= currentTs - graphBounds.from) : [];
        const devices = devList && devList.toJS ? devList.toJS() : devList;
        const phaseColorsUses = {
            p1: false,
            p2: false,
            p3: false
        };

        const datasets = [];
        if(selectedSw > 0){
            const switches = swList.filter(item => item.id === selectedSw);
            if(switches && switches.length > 0){
                const sw = switches[0];
                for(const relay of sw.relays){
                    if(relay.device !== null) {
                        const relayId = relay.relay >= 0 ? relay.relay : 0;
                        let fillColor = colorList[relayId].fill;
                        let borderColor = colorList[relayId].border;
                        const relayPhase = relay.phase;
                        if(relayPhase){
                            if(relayPhase === 1 && !phaseColorsUses.p1){
                                fillColor = graphColors.p1_fill;
                                borderColor = graphColors.p1_line;
                                phaseColorsUses.p1 = true;
                            }
                            if(relayPhase === 2 && !phaseColorsUses.p2){
                                fillColor = graphColors.p2_fill;
                                borderColor = graphColors.p2_line;
                                phaseColorsUses.p2 = true;
                            }
                            if(relayPhase === 3 && !phaseColorsUses.p3){
                                fillColor = graphColors.p3_fill;
                                borderColor = graphColors.p3_line;
                                phaseColorsUses.p3 = true;
                            }
                        }
                        let title = `${sw.brand}: ${relayId}`;
                        if(devices){
                            const dev = devices.filter(item => item.device === relay.device);
                            // console.log(dev);
                            if(dev && dev.length > 0) title = `${relayId}: ${dev[0].name}`;
                        }
                        datasets.push({
                            label: `${title} / ${strings.training.singleGraphUsage}`,
                            backgroundColor: fillColor,
                            borderColor,
                            borderWidth: 1,
                            lineTension: 0,
                            data: powerSingle.filter(item => item.relay === relay.relay).map(item => ({
                                x: item.id,
                                y: item.watt
                            }))
                        });
                        datasets.push({
                            label: `${title} / ${strings.training.singleGraphTemp}`,
                            backgroundColor: fillColor,
                            borderColor,
                            borderWidth: 1,
                            lineTension: 0,
                            borderDash: [1, 2],
                            data: powerSingle.filter(item => item.relay === relay.relay).map(item => ({
                                x: item.id,
                                y: item.temp
                            }))
                        });
                        datasets.push({
                            label: `${title} / ${strings.training.singleGraphStatus}`,
                            backgroundColor: borderColor,
                            borderColor,
                            borderWidth: 1,
                            lineTension: 0,
                            data: powerSingle.filter(item => item.relay === relay.relay).map(item => ({
                                x: item.id,
                                y: item.status
                            }))
                        });
                    }
                }
            }
        }
        datasets.push({
            label: 'HIDDEN',
            fill: false,
            borderColor: '#EEE',
            borderWidth: 1,
            lineTension: 0,
            data: [{x: currentTs - graphBounds.to, y: 0}, {x: currentTs - graphBounds.from, y: 0}]
        });

        return { datasets };
    }
);
